import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { IconButton } from '../components/IconButton'
import { Modal } from '../components/Modal'
import { SortableTh } from '../components/SortableTh'
import { useTableSort } from '../hooks/useTableSort'
import { formatMesReferencia, mesReferencia, ultimoDiaMesRef } from '../lib/pedidoMensal'
import {
  kgParaUnidadesComerciais,
} from '../lib/produtoConversaoKg'
import { fetchConsumoPrevisto } from '../lib/supabaseQuery'
import { supabase } from '../lib/supabase'
import { downloadText, formatDate, formatNumber, parseQuantidadeBr, toCsv } from '../lib/utils'
import type {
  AjusteEstoque,
  AlimentoMacronutriente,
  EntradaEstoque,
  Escola,
  Produto,
  Rota,
} from '../types/database'

interface EstoqueRow {
  escola_id: string
  alimento_id: string
  escola: string
  rota: string
  rota_id: string | null
  produto_nome: string
  categoria: string
  qnt_entregue_un: number | null
  consumo_estimado_un: number | null
  ajuste_un: number | null
  ajuste_kg: number
  saldo_un: number | null
  saldo_kg: number
}

type HistoricoRow = {
  id: string
  ajuste_id?: string
  alimento_id?: string
  produto_id?: string | null
  data: string
  escola_id: string
  escola_nome: string
  produto_nome: string
  categoria: string
  tipo: 'Entrega' | 'Ajuste'
  quantidade_un: number | null
  quantidade_kg: number
  observacoes: string
}

type EntradaAgg = Pick<EntradaEstoque, 'escola_id' | 'alimento_id' | 'quantidade' | 'data'>
type AjusteAgg = Pick<
  AjusteEstoque,
  'id' | 'escola_id' | 'alimento_id' | 'quantidade' | 'data' | 'mes_referencia' | 'motivo'
>

function rowKey(escolaId: string, alimentoId: string) {
  return `${escolaId}:${alimentoId}`
}

function parseRowKey(key: string): [string, string] | null {
  const sep = key.indexOf(':')
  if (sep <= 0) return null
  return [key.slice(0, sep), key.slice(sep + 1)]
}

function resolveProdutoId(
  produtoId: string,
  busca: string,
  produtos: Produto[],
): { produtoId: string | null; erro: string | null } {
  if (produtoId) return { produtoId, erro: null }
  const q = busca.trim().toLowerCase()
  if (!q) return { produtoId: null, erro: 'Selecione um produto na lista.' }
  const exact = produtos.filter((p) => p.nome.toLowerCase() === q)
  if (exact.length === 1) return { produtoId: exact[0].id, erro: null }
  const partial = produtos.filter((p) => p.nome.toLowerCase().includes(q))
  if (partial.length === 1) return { produtoId: partial[0].id, erro: null }
  if (partial.length > 1) {
    return { produtoId: null, erro: 'Vários produtos correspondem à busca. Clique no produto na lista.' }
  }
  return { produtoId: null, erro: 'Produto não encontrado. Clique no produto na lista.' }
}

function produtoDoAlimento(
  alimento: { produto_id?: string | null } | undefined,
  produtoById: Map<string, Produto>,
): Produto | undefined {
  if (!alimento?.produto_id) return undefined
  return produtoById.get(alimento.produto_id)
}

function alimentoIdPorProduto(
  produtoId: string,
  alimentos: AlimentoMacronutriente[],
): { alimentoId: string | null; erro: string | null } {
  const matches = alimentos.filter((a) => a.produto_id === produtoId)
  if (!matches.length) {
    return {
      alimentoId: null,
      erro: 'Produto sem macronutriente vinculado. Configure em Configurações → Macronutrientes.',
    }
  }
  if (matches.length > 1) {
    return {
      alimentoId: null,
      erro: 'Mais de um macronutriente vinculado a este produto. Corrija em Configurações.',
    }
  }
  return { alimentoId: matches[0].id, erro: null }
}

function kgParaUnNum(kg: number, produto: Produto | undefined): number | null {
  if (!produto || !Number.isFinite(kg)) return null
  if (Math.abs(kg) < 1e-12) return 0
  return kgParaUnidadesComerciais(kg, produto)
}

function isoDateOnly(value: string): string {
  return value.slice(0, 10)
}

/** Chave ano-mês (YYYY-MM) para mes_referencia do ajuste. */
function mesChave(value: string): string {
  return isoDateOnly(value).slice(0, 7)
}

function isMesRefAnterior(ref: string, mesInicio: string): boolean {
  return mesChave(ref) < mesChave(mesInicio)
}

function isMesRefAtual(ref: string, mesInicio: string): boolean {
  return mesChave(ref) === mesChave(mesInicio)
}

function isBeforeMonthDay(value: string, monthStart: string): boolean {
  return isoDateOnly(value) < isoDateOnly(monthStart)
}

function isInSelectedMonth(value: string, monthStart: string, monthEnd: string): boolean {
  const d = isoDateOnly(value)
  return d >= isoDateOnly(monthStart) && d <= isoDateOnly(monthEnd)
}

function hojeIsoLocal(): string {
  const hoje = new Date()
  const y = hoje.getFullYear()
  const m = String(hoje.getMonth() + 1).padStart(2, '0')
  const d = String(hoje.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Mês de referência do ajuste (YYYY-MM-01). Sem mes_referencia, ignora no saldo. */
function mesRefAjuste(row: Pick<AjusteEstoque, 'mes_referencia'>): string | null {
  if (!row.mes_referencia) return null
  return isoDateOnly(row.mes_referencia)
}

function formatQtdUn(un: number | null | undefined): string {
  if (un == null || !Number.isFinite(un)) return '—'
  return formatNumber(un, 3)
}

function formatQtdKgUn(kg: number, un: number | null | undefined): string {
  if (un != null && Number.isFinite(un)) return formatQtdUn(un)
  if (Number.isFinite(kg) && Math.abs(kg) > 1e-12) return `${formatNumber(kg, 3)} kg`
  if (kg === 0) return formatNumber(0, 3)
  return '—'
}

function addKg(map: Map<string, number>, key: string, kg: number) {
  if (!Number.isFinite(kg)) return
  map.set(key, (map.get(key) || 0) + kg)
}

export function EstoquePage() {
  const { canWrite, canDelete, profile } = useAuth()
  const hoje = useMemo(() => new Date(), [])
  const [mesAno, setMesAno] = useState({ year: hoje.getFullYear(), month: hoje.getMonth() + 1 })
  const mesRef = mesReferencia(mesAno.year, mesAno.month)
  const mesInicio = mesRef
  const mesFim = ultimoDiaMesRef(mesRef)

  const [rows, setRows] = useState<EstoqueRow[]>([])
  const [historico, setHistorico] = useState<HistoricoRow[]>([])
  const [escolas, setEscolas] = useState<Escola[]>([])
  const [alimentos, setAlimentos] = useState<AlimentoMacronutriente[]>([])
  const [produtos, setProdutos] = useState<Produto[]>([])
  const [rotas, setRotas] = useState<Rota[]>([])
  const [estoquesPorEscola, setEstoquesPorEscola] = useState<Record<string, string[]>>({})
  const [filtroEscolas, setFiltroEscolas] = useState<string[]>([])
  const [filtroProduto, setFiltroProduto] = useState('')
  const [escolaBusca, setEscolaBusca] = useState('')
  const [escolaComboOpen, setEscolaComboOpen] = useState(false)
  const [escolaHighlight, setEscolaHighlight] = useState(0)
  const escolaComboRef = useRef<HTMLDivElement>(null)
  const loadSeqRef = useRef(0)
  const [mastersLoading, setMastersLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [modalError, setModalError] = useState<string | null>(null)
  const [savingAjuste, setSavingAjuste] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const dataRegistroHoje = useMemo(() => hojeIsoLocal(), [modalOpen])
  const [editingAjusteId, setEditingAjusteId] = useState<string | null>(null)
  const [editingAjusteData, setEditingAjusteData] = useState<string | null>(null)
  const [produtoBusca, setProdutoBusca] = useState('')
  const [showHist, setShowHist] = useState(false)
  const [form, setForm] = useState({
    escola_id: '',
    produto_id: '',
    quantidade: '',
    motivo: '',
  })
  const canAjustar = canWrite('ajustes_estoque')
  const canExcluirAjuste = canDelete('ajustes_estoque')
  const showAcoesAjuste = canAjustar || canExcluirAjuste
  const estoqueSort = useTableSort<EstoqueRow>('escola', 'asc')
  const historicoSort = useTableSort<HistoricoRow>('data', 'desc')

  const produtoById = useMemo(() => new Map(produtos.map((p) => [p.id, p])), [produtos])
  const escolaById = useMemo(() => new Map(escolas.map((e) => [e.id, e])), [escolas])

  const escolasDisponiveis = useMemo(() => {
    const q = escolaBusca.trim().toLowerCase()
    return escolas
      .filter((e) => !filtroEscolas.includes(e.id))
      .filter((e) => !q || e.nome.toLowerCase().includes(q))
  }, [escolaBusca, escolas, filtroEscolas])

  const alimentosFiltro = useMemo(() => {
    if (!filtroEscolas.length) return []
    const ids = new Set<string>()
    for (const escolaId of filtroEscolas) {
      for (const alimentoId of estoquesPorEscola[escolaId] || []) ids.add(alimentoId)
    }
    for (const r of rows) {
      if (filtroEscolas.includes(r.escola_id)) ids.add(r.alimento_id)
    }
    return alimentos
      .filter((a) => ids.has(a.id))
      .sort((a, b) => {
        const na = produtoDoAlimento(a, produtoById)?.nome || a.nome
        const nb = produtoDoAlimento(b, produtoById)?.nome || b.nome
        return na.localeCompare(nb, 'pt-BR')
      })
  }, [alimentos, estoquesPorEscola, filtroEscolas, produtoById, rows])

  function addEscolaFiltro(escolaId: string) {
    if (!escolaId || filtroEscolas.includes(escolaId)) return
    setFiltroEscolas((prev) => [...prev, escolaId])
    setEscolaBusca('')
    setEscolaComboOpen(false)
    setEscolaHighlight(0)
  }

  function removeEscolaFiltro(escolaId: string) {
    setFiltroEscolas((prev) => prev.filter((id) => id !== escolaId))
  }

  useEffect(() => {
    setEscolaHighlight(0)
  }, [escolaBusca, escolasDisponiveis.length])

  useEffect(() => {
    if (!escolaComboOpen) return
    function onPointerDown(ev: MouseEvent) {
      if (!escolaComboRef.current?.contains(ev.target as Node)) {
        setEscolaComboOpen(false)
      }
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [escolaComboOpen])

  function onEscolaComboKeyDown(ev: { key: string; preventDefault: () => void }) {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault()
      setEscolaComboOpen(true)
      if (!escolasDisponiveis.length) return
      setEscolaHighlight((i) => Math.min(i + 1, escolasDisponiveis.length - 1))
      return
    }
    if (ev.key === 'ArrowUp') {
      ev.preventDefault()
      if (!escolasDisponiveis.length) return
      setEscolaHighlight((i) => Math.max(i - 1, 0))
      return
    }
    if (ev.key === 'Enter') {
      ev.preventDefault()
      const escolhida = escolasDisponiveis[escolaHighlight]
      if (escolhida) addEscolaFiltro(escolhida.id)
      return
    }
    if (ev.key === 'Escape') {
      setEscolaComboOpen(false)
    }
  }

  const produtosFiltradosModal = useMemo(() => {
    if (!form.escola_id) return []

    const alimentoIds = new Set(estoquesPorEscola[form.escola_id] || [])
    const produtoIds = new Set<string>()
    for (const alimento of alimentos) {
      if (alimento.produto_id && alimentoIds.has(alimento.id)) {
        produtoIds.add(alimento.produto_id)
      }
    }

    const q = produtoBusca.trim().toLowerCase()
    let list = produtos.filter((p) => produtoIds.has(p.id))
    if (form.produto_id && !list.some((p) => p.id === form.produto_id)) {
      const atual = produtos.find((p) => p.id === form.produto_id)
      if (atual) list = [atual, ...list]
    }
    list.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
    if (!q) return list
    return list.filter((p) => p.nome.toLowerCase().includes(q))
  }, [alimentos, estoquesPorEscola, form.escola_id, form.produto_id, produtoBusca, produtos])

  function closeAjusteModal() {
    setModalOpen(false)
    setEditingAjusteId(null)
    setEditingAjusteData(null)
    setProdutoBusca('')
    setModalError(null)
    setForm({ escola_id: '', produto_id: '', quantidade: '', motivo: '' })
  }

  function openAjusteModal() {
    setEditingAjusteId(null)
    setEditingAjusteData(null)
    setModalError(null)
    setError(null)
    setModalOpen(true)
  }

  function openEditAjuste(row: HistoricoRow) {
    if (row.tipo !== 'Ajuste' || !row.ajuste_id) return
    setEditingAjusteId(row.ajuste_id)
    setEditingAjusteData(row.data)
    setForm({
      escola_id: row.escola_id,
      produto_id: row.produto_id || '',
      quantidade: String(row.quantidade_kg),
      motivo: row.observacoes,
    })
    setProdutoBusca(row.produto_nome)
    setModalError(null)
    setError(null)
    setModalOpen(true)
  }

  async function removeAjuste(id: string) {
    if (!confirm('Excluir este ajuste de estoque?')) return
    const { error: err } = await supabase.from('ajustes_estoque').delete().eq('id', id)
    if (err) {
      setError(err.message)
      return
    }
    await load()
  }

  const loadMasters = useCallback(async () => {
    setMastersLoading(true)
    setError(null)
    const [esc, alim, pr, rot] = await Promise.all([
      supabase.from('escolas').select('*').order('nome'),
      supabase.from('alimentos_macronutrientes').select('*').order('nome'),
      supabase.from('produtos').select('*').order('nome'),
      supabase.from('rotas').select('*').order('nome'),
    ])
    if (esc.error || alim.error || pr.error || rot.error) {
      setError(
        esc.error?.message ||
          alim.error?.message ||
          pr.error?.message ||
          rot.error?.message ||
          'Erro ao carregar cadastros',
      )
      setMastersLoading(false)
      return
    }
    setEscolas((esc.data as Escola[]) || [])
    setAlimentos((alim.data as AlimentoMacronutriente[]) || [])
    setProdutos((pr.data as Produto[]) || [])
    setRotas((rot.data as Rota[]) || [])
    setMastersLoading(false)
  }, [])

  const load = useCallback(async () => {
    if (!filtroEscolas.length) {
      loadSeqRef.current++
      setRows([])
      setHistorico([])
      setEstoquesPorEscola({})
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    const seq = ++loadSeqRef.current

    const [entradasRes, ajustesRes, entradasHistRes, ajustesHistRes, consumoRes] = await Promise.all([
      supabase
        .from('entradas_estoque')
        .select('escola_id, alimento_id, quantidade, data')
        .in('escola_id', filtroEscolas)
        .lte('data', mesFim)
        .limit(10000),
      supabase
        .from('ajustes_estoque')
        .select('id, escola_id, alimento_id, quantidade, data, mes_referencia, motivo')
        .in('escola_id', filtroEscolas)
        .lte('mes_referencia', mesFim)
        .limit(10000),
      supabase
        .from('entradas_estoque')
        .select(
          '*, escola:escolas(nome), alimento:alimentos_macronutrientes(nome, categoria, produto_id)',
        )
        .in('escola_id', filtroEscolas)
        .gte('data', mesInicio)
        .lte('data', mesFim)
        .order('data', { ascending: false })
        .limit(500),
      supabase
        .from('ajustes_estoque')
        .select(
          '*, escola:escolas(nome), alimento:alimentos_macronutrientes(nome, categoria, produto_id)',
        )
        .in('escola_id', filtroEscolas)
        .gte('mes_referencia', mesInicio)
        .lte('mes_referencia', mesFim)
        .order('data', { ascending: false })
        .limit(500),
      fetchConsumoPrevisto({
        inicio: '2020-01-01',
        fim: mesFim,
        escolaIds: filtroEscolas,
      }),
    ])
    if (seq !== loadSeqRef.current) return

    if (entradasRes.error || ajustesRes.error) {
      setError(
        entradasRes.error?.message ||
          ajustesRes.error?.message ||
          'Erro ao carregar estoque',
      )
      setLoading(false)
      return
    }
    if (consumoRes.error) {
      setError(consumoRes.error)
      setLoading(false)
      return
    }

    const histWarnings: string[] = []
    if (entradasHistRes.error) histWarnings.push(`Histórico de entregas: ${entradasHistRes.error.message}`)
    if (ajustesHistRes.error) histWarnings.push(`Histórico de ajustes: ${ajustesHistRes.error.message}`)

    const escolasData = escolas
    const alimentosData = alimentos
    const produtosData = produtos
    const prodMap = new Map(produtosData.map((p) => [p.id, p]))
    const alimMap = new Map(alimentosData.map((a) => [a.id, a]))
    const rotasData = rotas

    const entregueAntes = new Map<string, number>()
    const entregueMes = new Map<string, number>()
    for (const row of (entradasRes.data || []) as EntradaAgg[]) {
      const key = rowKey(row.escola_id, row.alimento_id)
      const kg = Number(row.quantidade)
      if (isBeforeMonthDay(row.data, mesInicio)) addKg(entregueAntes, key, kg)
      else if (isInSelectedMonth(row.data, mesInicio, mesFim)) addKg(entregueMes, key, kg)
    }

    const ajusteAntes = new Map<string, number>()
    const ajusteMes = new Map<string, number>()
    for (const row of (ajustesRes.data || []) as AjusteAgg[]) {
      const ref = mesRefAjuste(row)
      if (!ref) continue
      const key = rowKey(row.escola_id, row.alimento_id)
      const kg = Number(row.quantidade)
      if (!Number.isFinite(kg)) continue
      if (isMesRefAnterior(ref, mesInicio)) addKg(ajusteAntes, key, kg)
      else if (isMesRefAtual(ref, mesInicio)) addKg(ajusteMes, key, kg)
    }

    const consumoAntes = new Map<string, number>()
    const consumoMesMap = new Map<string, number>()
    for (const c of consumoRes.data) {
      const key = rowKey(c.escola_id, c.alimento_id)
      const kg = Number(c.quantidade || 0) / 1000
      if (isBeforeMonthDay(c.data, mesInicio)) addKg(consumoAntes, key, kg)
      else if (isInSelectedMonth(c.data, mesInicio, mesFim)) addKg(consumoMesMap, key, kg)
    }

    const keys = new Set<string>([
      ...entregueAntes.keys(),
      ...entregueMes.keys(),
      ...ajusteAntes.keys(),
      ...ajusteMes.keys(),
      ...consumoAntes.keys(),
      ...consumoMesMap.keys(),
    ])

    const stockByEscola = new Map<string, Set<string>>()
    const built: EstoqueRow[] = []
    for (const key of keys) {
      const parsed = parseRowKey(key)
      if (!parsed) continue
      const [escolaId, alimentoId] = parsed
      const escola = escolasData.find((e) => e.id === escolaId)
      const alimento = alimMap.get(alimentoId)
      if (!escola || !alimento) continue

      const produto = produtoDoAlimento(alimento, prodMap)
      const entMesKg = entregueMes.get(key) || 0
      const consMesKg = consumoMesMap.get(key) || 0
      const ajMesKg = ajusteMes.get(key) || 0
      const netAntesKg =
        (entregueAntes.get(key) || 0) - (consumoAntes.get(key) || 0) + (ajusteAntes.get(key) || 0)
      const saldoKg = netAntesKg + entMesKg - consMesKg + ajMesKg

      const hasMes =
        Math.abs(entMesKg) > 1e-9 || Math.abs(consMesKg) > 1e-9 || Math.abs(ajMesKg) > 1e-9
      const hasSaldo = Math.abs(saldoKg) > 1e-9
      if (!hasMes && !hasSaldo) continue

      let stockSet = stockByEscola.get(escolaId)
      if (!stockSet) {
        stockSet = new Set<string>()
        stockByEscola.set(escolaId, stockSet)
      }
      stockSet.add(alimentoId)

      const rota = escola.rota_id ? rotasData.find((r) => r.id === escola.rota_id) : null
      built.push({
        escola_id: escolaId,
        alimento_id: alimentoId,
        escola: escola.nome,
        rota: rota?.nome || '—',
        rota_id: escola.rota_id,
        produto_nome: produto?.nome || alimento.nome,
        categoria: alimento.categoria || '—',
        qnt_entregue_un: kgParaUnNum(entMesKg, produto),
        consumo_estimado_un: kgParaUnNum(consMesKg, produto),
        ajuste_un: kgParaUnNum(ajMesKg, produto),
        ajuste_kg: ajMesKg,
        saldo_un: kgParaUnNum(saldoKg, produto),
        saldo_kg: saldoKg,
      })
    }

    setEstoquesPorEscola(
      Object.fromEntries([...stockByEscola.entries()].map(([id, set]) => [id, [...set]])),
    )

    setRows(built)

    const histEntradas: HistoricoRow[] = ((entradasHistRes.data || []) as (EntradaEstoque & {
      escola?: { nome: string }
      alimento?: { nome: string; categoria?: string; produto_id?: string | null }
    })[]).map((h) => {
      const alimRow = h.alimento || alimMap.get(h.alimento_id)
      const prod = produtoDoAlimento(alimRow, prodMap)
      const kg = Number(h.quantidade)
      return {
        id: `entrada-${h.id}`,
        data: isoDateOnly(h.data),
        escola_id: h.escola_id,
        escola_nome: h.escola?.nome || escolasData.find((e) => e.id === h.escola_id)?.nome || '—',
        produto_nome: prod?.nome || alimRow?.nome || '—',
        categoria: alimRow?.categoria || '—',
        tipo: 'Entrega' as const,
        quantidade_un: kgParaUnNum(kg, prod),
        quantidade_kg: kg,
        observacoes: h.observacoes?.trim() || 'Entrada no estoque via entrega',
      }
    })

    const histAjustes: HistoricoRow[] = ((ajustesHistRes.data || []) as (AjusteEstoque & {
      escola?: { nome: string }
      alimento?: { nome: string; categoria?: string; produto_id?: string | null }
    })[]).map((h) => {
      const alimRow = h.alimento || alimMap.get(h.alimento_id)
      const prod = produtoDoAlimento(alimRow, prodMap)
      const kg = Number(h.quantidade)
      return {
        id: `ajuste-${h.id}`,
        ajuste_id: h.id,
        alimento_id: h.alimento_id,
        produto_id: alimRow?.produto_id,
        data: isoDateOnly(h.data),
        escola_id: h.escola_id,
        escola_nome: h.escola?.nome || escolasData.find((e) => e.id === h.escola_id)?.nome || '—',
        produto_nome: prod?.nome || alimRow?.nome || '—',
        categoria: alimRow?.categoria || '—',
        tipo: 'Ajuste' as const,
        quantidade_un: kgParaUnNum(kg, prod),
        quantidade_kg: kg,
        observacoes: h.motivo.trim(),
      }
    })

    setHistorico([...histEntradas, ...histAjustes])
    if (histWarnings.length) {
      setError(histWarnings.join(' · '))
    }
    setLoading(false)
  }, [alimentos, escolas, filtroEscolas, mesFim, mesInicio, produtos, rotas])

  useEffect(() => {
    document.title = 'Estoque · ISEN EDUCAE'
  }, [])

  useEffect(() => {
    void loadMasters()
  }, [loadMasters])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (loading) return
    if (filtroProduto && !alimentosFiltro.some((a) => a.id === filtroProduto)) {
      setFiltroProduto('')
    }
  }, [alimentosFiltro, filtroProduto, loading])

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (filtroEscolas.length && !filtroEscolas.includes(r.escola_id)) return false
      if (filtroProduto && r.alimento_id !== filtroProduto) return false
      return true
    })
  }, [rows, filtroEscolas, filtroProduto])

  const filteredSorted = useMemo(() => {
    const accessors: Record<string, (r: EstoqueRow) => string | number> = {
      escola: (r) => r.escola,
      rota: (r) => r.rota,
      produto: (r) => r.produto_nome,
      categoria: (r) => r.categoria,
      entregue: (r) => r.qnt_entregue_un ?? -1,
      consumo: (r) => r.consumo_estimado_un ?? -1,
      ajustes: (r) => r.ajuste_un ?? r.ajuste_kg,
      saldo: (r) => r.saldo_un ?? r.saldo_kg,
    }
    return estoqueSort.sortRows(filtered, accessors)
  }, [filtered, estoqueSort])

  const historicoFiltrado = useMemo(() => {
    return historico.filter((h) => {
      if (filtroEscolas.length && !filtroEscolas.includes(h.escola_id)) return false
      return true
    })
  }, [historico, filtroEscolas])

  const historicoSorted = useMemo(() => {
    const accessors: Record<string, (h: HistoricoRow) => string | number> = {
      data: (h) => h.data,
      escola: (h) => h.escola_nome,
      produto: (h) => h.produto_nome,
      categoria: (h) => h.categoria,
      tipo: (h) => h.tipo,
      qtd: (h) => h.quantidade_un ?? h.quantidade_kg,
      obs: (h) => h.observacoes,
    }
    return historicoSort.sortRows(historicoFiltrado, accessors)
  }, [historicoFiltrado, historicoSort])

  async function saveAjuste() {
    if (!profile || savingAjuste) return
    setModalError(null)

    if (!form.escola_id) {
      setModalError('Selecione a escola.')
      return
    }

    const { produtoId, erro: produtoErro } = resolveProdutoId(
      form.produto_id,
      produtoBusca,
      produtos,
    )
    if (produtoErro || !produtoId) {
      setModalError(produtoErro || 'Selecione um produto na lista.')
      return
    }

    if (form.quantidade.trim() === '') {
      setModalError('Informe a quantidade em kg.')
      return
    }

    const qtdKg = parseQuantidadeBr(form.quantidade)
    if (Number.isNaN(qtdKg)) {
      setModalError('Quantidade inválida. Use ponto ou vírgula (ex.: 2,5 ou -0,5).')
      return
    }
    if (qtdKg === 0) {
      setModalError('Quantidade não pode ser zero.')
      return
    }
    if (!form.motivo.trim()) {
      setModalError('Informe o motivo do ajuste.')
      return
    }

    const { alimentoId, erro: vinculoErro } = alimentoIdPorProduto(produtoId, alimentos)
    if (vinculoErro || !alimentoId) {
      setModalError(vinculoErro || 'Macronutriente não encontrado para o produto.')
      return
    }

    setSavingAjuste(true)
    let err: { message: string } | null = null

    if (editingAjusteId) {
      const { error } = await supabase
        .from('ajustes_estoque')
        .update({
          escola_id: form.escola_id,
          alimento_id: alimentoId,
          quantidade: qtdKg,
          motivo: form.motivo.trim(),
        })
        .eq('id', editingAjusteId)
      err = error
    } else {
      const dataRegistro = hojeIsoLocal()
      const { data: inserted, error } = await supabase
        .from('ajustes_estoque')
        .insert({
          escola_id: form.escola_id,
          alimento_id: alimentoId,
          quantidade: qtdKg,
          motivo: form.motivo.trim(),
          registrado_por: profile.id,
          data: dataRegistro,
          mes_referencia: mesRef,
        })
        .select('id')
        .single()
      err = error
      if (!err && !inserted?.id) {
        err = { message: 'Ajuste não foi gravado. Verifique permissões e tente novamente.' }
      }
    }

    setSavingAjuste(false)

    if (err) {
      setModalError(err.message)
      return
    }

    closeAjusteModal()
    await load()
  }

  function exportCsv() {
    if (showHist) {
      downloadText(
        `estoque-historico-${mesRef.slice(0, 7)}.csv`,
        toCsv(
          historicoFiltrado.map((h) => ({
            Mês: formatMesReferencia(mesRef),
            Data: h.data,
            Escola: h.escola_nome,
            Produto: h.produto_nome,
            Categoria: h.categoria,
            Tipo: h.tipo,
            'Qtd': formatQtdKgUn(h.quantidade_kg, h.quantidade_un),
            Obs: h.observacoes,
          })),
        ),
      )
      return
    }
    downloadText(
      `estoque-${mesRef.slice(0, 7)}.csv`,
      toCsv(
        filtered.map((r) => ({
          Mês: formatMesReferencia(mesRef),
          Escola: r.escola,
          Rota: r.rota,
          Produto: r.produto_nome,
          Categoria: r.categoria,
          'Qnt entregue (un)': formatQtdUn(r.qnt_entregue_un),
          'Consumo estimado (un)': formatQtdUn(r.consumo_estimado_un),
          'Ajuste (un)': formatQtdKgUn(r.ajuste_kg, r.ajuste_un),
          'Saldo (un)': formatQtdKgUn(r.saldo_kg, r.saldo_un),
        })),
      ),
    )
  }

  if (mastersLoading) return <div className="loading">Carregando…</div>

  const escolasSelecionadasLabel =
    filtroEscolas.length === 0
      ? 'nenhuma escola selecionada'
      : filtroEscolas.length === 1
        ? escolaById.get(filtroEscolas[0])?.nome || 'escola'
        : `${filtroEscolas.length} escolas`

  return (
    <div>
      <div className="page-header">
        <h1>Estoque</h1>
        <div className="page-actions">
          <div className="pill-toggle">
            <button type="button" className={!showHist ? 'active' : ''} onClick={() => setShowHist(false)}>
              Saldo
            </button>
            <button type="button" className={showHist ? 'active' : ''} onClick={() => setShowHist(true)}>
              Histórico
            </button>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={exportCsv}
            disabled={!filtroEscolas.length}
          >
            Exportar CSV
          </button>
          {canAjustar && (
            <button type="button" className="btn btn-primary" onClick={openAjusteModal}>
              Ajustar/baixar
            </button>
          )}
        </div>
      </div>
      {error && <div className="alert alert-error">{error}</div>}

      <div className="filters filters--estoque">
        <div className="field">
          <label>Mês *</label>
          <input
            type="month"
            value={mesRef.slice(0, 7)}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map(Number)
              if (y && m) setMesAno({ year: y, month: m })
            }}
            required
          />
        </div>
        <div className="field">
          <label>Escolas *</label>
          <div className="estoque-escola-combo" ref={escolaComboRef}>
            <input
              type="text"
              role="combobox"
              aria-expanded={escolaComboOpen}
              aria-controls="estoque-escola-listbox"
              aria-autocomplete="list"
              placeholder={
                mastersLoading
                  ? 'Carregando escolas…'
                  : !escolas.length
                    ? 'Nenhuma escola cadastrada'
                    : 'Digite para buscar e adicionar escola…'
              }
              value={escolaBusca}
              onChange={(e) => {
                setEscolaBusca(e.target.value)
                setEscolaComboOpen(true)
              }}
              onFocus={() => setEscolaComboOpen(true)}
              onKeyDown={onEscolaComboKeyDown}
              disabled={mastersLoading || !escolas.length}
              autoComplete="off"
            />
            {escolaComboOpen && !mastersLoading && !!escolas.length && (
              <ul
                id="estoque-escola-listbox"
                className="estoque-escola-combo-list"
                role="listbox"
                aria-label="Escolas"
              >
                {escolasDisponiveis.length ? (
                  escolasDisponiveis.map((e, idx) => (
                    <li key={e.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={idx === escolaHighlight}
                        className={`estoque-escola-combo-option${idx === escolaHighlight ? ' selected' : ''}`}
                        onMouseEnter={() => setEscolaHighlight(idx)}
                        onMouseDown={(ev) => {
                          ev.preventDefault()
                          addEscolaFiltro(e.id)
                        }}
                      >
                        {e.nome}
                      </button>
                    </li>
                  ))
                ) : (
                  <li className="estoque-escola-combo-empty">
                    {escolaBusca.trim()
                      ? 'Nenhuma escola encontrada'
                      : 'Todas as escolas já foram adicionadas'}
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
        {!showHist && (
          <div className="field">
            <label>Produto</label>
            <select
              value={filtroProduto}
              onChange={(e) => setFiltroProduto(e.target.value)}
              disabled={!filtroEscolas.length}
            >
              <option value="">Todos</option>
              {alimentosFiltro.map((a) => {
                const prod = produtoDoAlimento(a, produtoById)
                return (
                  <option key={a.id} value={a.id}>
                    {prod?.nome || a.nome}
                  </option>
                )
              })}
            </select>
          </div>
        )}
      </div>
      {filtroEscolas.length > 0 && (
        <div className="estoque-escola-chips">
          {filtroEscolas.map((id) => (
            <button
              key={id}
              type="button"
              className="estoque-escola-chip"
              onClick={() => removeEscolaFiltro(id)}
              title="Remover escola"
            >
              {escolaById.get(id)?.nome || 'Escola'}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}
      <p className="kpi-sub" style={{ margin: '0 0 12px' }}>
        Valores em unidades comerciais · consumo estimado referente a {formatMesReferencia(mesRef)}
        {` · ${escolasSelecionadasLabel}`}. Saldo acumula o que sobra ou falta dos meses anteriores.
      </p>

      {!filtroEscolas.length && (
        <div className="alert" style={{ marginBottom: 16 }}>
          Selecione uma ou mais escolas para carregar o estoque. Você pode adicionar várias escolas à
          visualização.
        </div>
      )}

      {loading && <div className="loading">Carregando estoque…</div>}

      {!loading && filtroEscolas.length > 0 && !showHist && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Escola" sortKey="escola" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="name" />
                <SortableTh label="Rota" sortKey="rota" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="text" />
                <SortableTh label="Produto" sortKey="produto" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="name" />
                <SortableTh label="Categoria" sortKey="categoria" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="compact" />
                <SortableTh label="Qnt produto entregue (un)" sortKey="entregue" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="num" />
                <SortableTh label="Consumo estimado" sortKey="consumo" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="num" />
                <SortableTh label="Ajuste" sortKey="ajustes" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="num" />
                <SortableTh label="Saldo" sortKey="saldo" activeKey={estoqueSort.sortKey} direction={estoqueSort.sortDir} onSort={estoqueSort.toggleSort} col="num" />
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((r) => (
                <tr key={`${r.escola_id}-${r.alimento_id}`}>
                  <td className="col-name">{r.escola}</td>
                  <td className="col-text">{r.rota}</td>
                  <td className="col-name">{r.produto_nome}</td>
                  <td className="col-compact">{r.categoria}</td>
                  <td className="col-num">{formatQtdUn(r.qnt_entregue_un)}</td>
                  <td className="col-num">{formatQtdUn(r.consumo_estimado_un)}</td>
                  <td className="col-num">{formatQtdKgUn(r.ajuste_kg, r.ajuste_un)}</td>
                  <td className="col-num">{formatQtdKgUn(r.saldo_kg, r.saldo_un)}</td>
                </tr>
              ))}
              {!filteredSorted.length && (
                <tr>
                  <td colSpan={8} className="kpi-sub">
                    Nenhum saldo para os filtros selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && filtroEscolas.length > 0 && showHist && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh label="Data" sortKey="data" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="date" />
                <SortableTh label="Escola" sortKey="escola" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="name" />
                <SortableTh label="Produto" sortKey="produto" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="name" />
                <SortableTh label="Categoria" sortKey="categoria" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="compact" />
                <SortableTh label="Tipo" sortKey="tipo" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="compact" />
                <SortableTh label="Qtd (un)" sortKey="qtd" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="num" />
                <SortableTh label="Obs." sortKey="obs" activeKey={historicoSort.sortKey} direction={historicoSort.sortDir} onSort={historicoSort.toggleSort} col="text" />
                {showAcoesAjuste && <th className="col-actions" />}
              </tr>
            </thead>
            <tbody>
              {historicoSorted.map((h) => (
                <tr key={h.id}>
                  <td className="col-date">{formatDate(h.data)}</td>
                  <td className="col-name">{h.escola_nome}</td>
                  <td className="col-name">{h.produto_nome}</td>
                  <td className="col-compact">{h.categoria}</td>
                  <td className="col-compact">{h.tipo}</td>
                  <td className="col-num">{formatQtdKgUn(h.quantidade_kg, h.quantidade_un)}</td>
                  <td className="col-text">{h.observacoes}</td>
                  {showAcoesAjuste && (
                    <td className="col-actions">
                      {h.tipo === 'Ajuste' && h.ajuste_id ? (
                        <div className="table-actions">
                          {canAjustar && (
                            <IconButton
                              variant="edit"
                              label={`Editar ajuste ${h.produto_nome}`}
                              onClick={() => openEditAjuste(h)}
                            />
                          )}
                          {canExcluirAjuste && (
                            <IconButton
                              variant="delete"
                              label={`Excluir ajuste ${h.produto_nome}`}
                              onClick={() => removeAjuste(h.ajuste_id!)}
                            />
                          )}
                        </div>
                      ) : null}
                    </td>
                  )}
                </tr>
              ))}
              {!historicoSorted.length && (
                <tr>
                  <td colSpan={showAcoesAjuste ? 8 : 7} className="kpi-sub">
                    Nenhuma movimentação no mês/escola selecionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={modalOpen}
        title={
          editingAjusteId
            ? `Editar ajuste · ${formatMesReferencia(mesRef)}`
            : `Ajustar ou baixar estoque · ${formatMesReferencia(mesRef)}`
        }
        onClose={closeAjusteModal}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={closeAjusteModal}>
              Cancelar
            </button>
            <button type="button" className="btn btn-primary" onClick={saveAjuste} disabled={savingAjuste}>
              {savingAjuste ? 'Salvando…' : editingAjusteId ? 'Salvar alterações' : 'Salvar'}
            </button>
          </>
        }
      >
        <div className="form-grid">
          {modalError && (
            <div className="alert alert-error" style={{ gridColumn: '1 / -1' }}>
              {modalError}
            </div>
          )}
          <p className="kpi-sub" style={{ gridColumn: '1 / -1', margin: 0 }}>
            Mês de referência do estoque: <strong>{formatMesReferencia(mesRef)}</strong>
            {editingAjusteId && editingAjusteData ? (
              <>
                {' '}
                · registrado em: <strong>{formatDate(editingAjusteData)}</strong>
              </>
            ) : (
              <>
                {' '}
                · registrado em: <strong>{formatDate(dataRegistroHoje)}</strong> (data de hoje)
              </>
            )}
          </p>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Escola *</label>
            <select
              value={form.escola_id}
              onChange={(e) => {
                setForm({ escola_id: e.target.value, produto_id: '', quantidade: '', motivo: '' })
                setProdutoBusca('')
              }}
            >
              <option value="">Selecione</option>
              {escolas.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nome}
                </option>
              ))}
            </select>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Produto *</label>
            <input
              type="search"
              value={produtoBusca}
              onChange={(e) => {
                setProdutoBusca(e.target.value)
                setForm((prev) => ({ ...prev, produto_id: '' }))
              }}
              placeholder="Digite o nome do produto…"
            />
            <div
              className="table-wrap"
              style={{
                maxHeight: 200,
                marginTop: 8,
                border: '1px solid var(--color-border)',
                borderRadius: 8,
              }}
              role="listbox"
              aria-label="Produtos da Tabela de Preços"
            >
              <table className="data-table">
                <tbody>
                  {produtosFiltradosModal.map((p) => {
                    const selected = form.produto_id === p.id
                    return (
                      <tr
                        key={p.id}
                        role="option"
                        aria-selected={selected}
                        onClick={() => {
                          setForm({ ...form, produto_id: p.id })
                          setProdutoBusca(p.nome)
                          setModalError(null)
                        }}
                        style={{
                          cursor: 'pointer',
                          background: selected ? 'var(--color-surface-muted, #f4f4f5)' : undefined,
                        }}
                      >
                        <td className="col-name">{p.nome}</td>
                      </tr>
                    )
                  })}
                  {!produtosFiltradosModal.length && (
                    <tr>
                      <td className="kpi-sub">Nenhum produto encontrado.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {!form.escola_id && (
              <p className="kpi-sub" style={{ marginTop: 6 }}>
                Selecione a escola para listar os produtos em estoque.
              </p>
            )}
            {form.escola_id && !produtosFiltradosModal.length && (
              <p className="kpi-sub" style={{ marginTop: 6 }}>
                Nenhum produto em estoque nesta escola.
              </p>
            )}
            {form.produto_id ? (
              <p className="kpi-sub" style={{ marginTop: 6 }}>
                Selecionado: <strong>{produtoById.get(form.produto_id)?.nome}</strong>
              </p>
            ) : (
              <p className="kpi-sub" style={{ marginTop: 6 }}>
                Clique em um produto na lista para selecionar.
              </p>
            )}
          </div>
          <div className="field">
            <label>Quantidade em Kg *</label>
            <input
              type="number"
              step="any"
              value={form.quantidade}
              onChange={(e) => setForm({ ...form, quantidade: e.target.value })}
              placeholder="Ex.: 2,5 ou -0,5 para baixa"
            />
            <span className="kpi-sub">Valor negativo para baixa de estoque.</span>
          </div>
          <div className="field" style={{ gridColumn: '1 / -1' }}>
            <label>Motivo *</label>
            <textarea
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              placeholder="Ex.: perda, correção de inventário, consumo não previsto…"
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}
